import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { styled } from '@mui/material/styles';

import LogoRelif from 'assets/images/relifCompanies/big-relif-logo.webp';
import { PrimaryBtn } from 'components/UI/Buttons';
import useIsRelif from 'hooks/useIsRelif';

import NavigationTopBar from './NavigationTopBar';

const LinkButton = styled(Button)(({ theme }) => ({
  color: '#15183A',
  fontSize: '17px',
  fontFamily: 'Roboto',
  padding: '6px 8px',
  marginLeft: '8px',
  marginRight: '8px',
  display: 'inline-flex',
  justifyContent: 'center',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&:before': {
    content: "''",
    position: 'absolute',
    height: '3px',
    bottom: '4px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.palette.secondary.main,
    visibility: 'hidden',
    transition: 'all 0.2s ease',
    width: '0',
  },
  '&:hover:before': {
    visibility: 'visible',
    width: 'calc(100% - 16px)', // Subtracting the left and right margin
  },
}));

const BlueSeparator = () => (
  <svg
    width="80%"
    preserveAspectRatio="none"
    height="2"
    viewBox="0 0 180 2"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0 1.00003L180 1.00005" stroke="#638DB8" strokeWidth="2" />
  </svg>
);

interface Props {
  handleOpenFinancialAdvisorAuthModal: (isExistingUser: boolean) => void;
  type: 'Relif' | 'Planer' | 'Zeller' | 'Siker';
}

const Navigation: FC<Props> = ({ handleOpenFinancialAdvisorAuthModal, type }) => {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const { isRelif } = useIsRelif();

  const renderNavigationLinks = () => {
    if (isRelif) {
      return (
        <>
          <LinkButton onClick={() => navigate('/quienes-somos')}>Quiénes somos</LinkButton>
          <LinkButton onClick={() => navigate('/contactanos')}>Contáctanos</LinkButton>
        </>
      );
    }
    return (
      <>
        <LinkButton onClick={() => navigate('/simulador')}>Simulador</LinkButton>
        <LinkButton onClick={() => navigate('/landing-chat')}>Chat</LinkButton>
      </>
    );
  };

  const renderMobileNavigationLinks = () => {
    if (isRelif) {
      return (
        <>
          <Button
            variant="text"
            onClick={() => {
              navigate('/quienes-somos');
              setOpenMenu(false);
            }}
            sx={{
              fontFamily: 'Jost',
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: '25px',
            }}
          >
            Quiénes somos
          </Button>
          <BlueSeparator />
          <Button
            variant="text"
            onClick={() => {
              navigate('/contactanos');
              setOpenMenu(false);
            }}
            sx={{
              fontFamily: 'Jost',
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: '25px',
            }}
          >
            Contáctanos
          </Button>
        </>
      );
    }
    return (
      <>
        <Button
          variant="text"
          onClick={() => {
            navigate('/simulador');
            setOpenMenu(false);
          }}
          sx={{
            fontFamily: 'Jost',
            fontWeight: 400,
            fontSize: '15px',
            lineHeight: '25px',
          }}
        >
          Simulador
        </Button>
        <BlueSeparator />
        <Button
          variant="text"
          onClick={() => {
            navigate('/landing-chat');
            setOpenMenu(false);
          }}
          sx={{
            fontFamily: 'Jost',
            fontWeight: 400,
            fontSize: '15px',
            lineHeight: '25px',
          }}
        >
          Chat
        </Button>
      </>
    );
  };

  return (
    <AppBar
      sx={{
        variant: 'h2',
        backgroundColor: 'white',
        boxShadow: 'none',
      }}
    >
      <Box sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.15)' }}>
        <NavigationTopBar />
      </Box>
      <Container>
        <Box my={1} display="flex" justifyContent="space-between">
          <Box display="flex">
            <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/welcome')} pt={1}>
              <img
                src={isRelif ? LogoRelif : '/logo.webp'}
                alt="Logo"
                style={{
                  maxWidth: '200px',
                }}
              />
            </Box>
            <Box display={{ xs: 'none', md: 'flex' }} alignItems="center" pl={{ md: 0, lg: 4 }}>
              {renderNavigationLinks()}
            </Box>
          </Box>
          <Box display={{ xs: 'none', md: 'flex' }}>
            <Box justifyContent="center" alignItems="center" gap={2} display="flex">
              <PrimaryBtn
                onClick={() => {
                  if (type === 'Relif') {
                    navigate('/contactanos');
                  } else {
                    handleOpenFinancialAdvisorAuthModal(true);
                  }
                }}
                sx={
                  type === 'Relif'
                    ? {
                        backgroundColor: '#F9D00A',
                        color: 'black',
                        '&:hover': {
                          backgroundColor: '#DCB705',
                        },
                      }
                    : undefined
                }
              >
                {type === 'Relif' ? 'Hablemos' : 'Iniciar sesión'}
              </PrimaryBtn>
            </Box>
          </Box>
          <Box display={{ xs: 'flex', md: 'none' }}>
            <IconButton onClick={() => setOpenMenu(true)}>
              <MenuIcon sx={{ color: 'white', fontSize: '32px' }} />
            </IconButton>
            <SwipeableDrawer
              open={openMenu}
              onClose={() => setOpenMenu(false)}
              onOpen={() => setOpenMenu(true)}
              anchor="right"
            >
              <Stack spacing={2} alignItems="center" py={8} px={3}>
                {renderMobileNavigationLinks()}
                <BlueSeparator />
                <PrimaryBtn
                  variant="outlined"
                  sx={
                    type === 'Relif'
                      ? {
                          color: 'black',
                          backgroundColor: '#F9D00A',
                          borderColor: '#F9D00A',
                          '&:hover': {
                            backgroundColor: '#DCB705',
                            borderColor: '#DCB705',
                            color: 'black',
                          },
                        }
                      : {
                          color: 'black',
                          backgroundColor: '#F9D00A',
                          borderColor: '#F9D00A',
                          '&:hover': {
                            backgroundColor: '#DCB705',
                            borderColor: '#DCB705',
                            color: 'black',
                          },
                        }
                  }
                  onClick={() => {
                    if (type === 'Relif') {
                      navigate('/contactanos');
                      setOpenMenu(false);
                    } else {
                      handleOpenFinancialAdvisorAuthModal(true);
                      setOpenMenu(false);
                    }
                  }}
                >
                  {type === 'Relif' ? 'Hablemos' : 'Iniciar sesión'}
                </PrimaryBtn>
              </Stack>
            </SwipeableDrawer>
          </Box>
        </Box>
      </Container>
    </AppBar>
  );
};

export default Navigation;
